import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface MarkAsReadProps {
  book: {
    id: number
    title: string
    author: string
    isbn?: string
    cover_url?: string
    description?: string
    first_publish_year?: number
    number_of_pages?: number
  }
  toReadBookId: number
  onClose: () => void
  onSuccess: () => void
}

interface Club {
  id: number
  name: string
}

export default function MarkAsRead({ book, toReadBookId, onClose, onSuccess }: MarkAsReadProps) {
  const [clubs, setClubs] = useState<Club[]>([])
  const [selectedClubId, setSelectedClubId] = useState<number | 'personal' | null>(null)
  const [startedAt, setStartedAt] = useState('')
  const [completedAt, setCompletedAt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [loadingClubs, setLoadingClubs] = useState(true)

  useEffect(() => {
    fetchClubs()
  }, [])

  const fetchClubs = async () => {
    try {
      const response = await fetch('/api/v1/clubs')
      if (response.ok) {
        const data = await response.json()
        setClubs(data.clubs)
        // Auto-select personal if no clubs
        if (data.clubs.length === 0) {
          setSelectedClubId('personal')
        }
      } else {
        setError('Failed to load clubs')
      }
    } catch (err) {
      setError('Failed to load clubs')
    } finally {
      setLoadingClubs(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedClubId) {
      setError('Please select where you read this book')
      return
    }

    if (!completedAt) {
      setError('Please enter when you completed reading')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Create the book pick
      const url = selectedClubId === 'personal'
        ? '/api/v1/personal_readings'
        : `/api/v1/clubs/${selectedClubId}/book_picks`

      // Send dates with explicit format to avoid timezone issues
      const bookPickResponse = await fetchWithCsrf(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_pick: {
            status: 'completed',
            started_at: startedAt || null,
            completed_at: completedAt,
            is_historical: selectedClubId !== 'personal',
            unclaimed: false,
            book_attributes: {
              title: book.title,
              author: book.author,
              isbn: book.isbn,
              description: book.description,
              cover_url: book.cover_url,
              first_publish_year: book.first_publish_year,
              number_of_pages: book.number_of_pages
            }
          }
        })
      })

      if (!bookPickResponse.ok) {
        const data = await bookPickResponse.json()
        setError(data.errors?.join(', ') || 'Failed to mark as read')
        setLoading(false)
        return
      }

      // Remove from to-read list
      const deleteResponse = await fetchWithCsrf(`/api/v1/to_read_books/${toReadBookId}`, {
        method: 'DELETE'
      })

      if (deleteResponse.ok) {
        onSuccess()
        onClose()
      } else {
        setError('Failed to remove from reading list')
      }
    } catch (err) {
      setError('Failed to mark as read')
    } finally {
      setLoading(false)
    }
  }

  if (loadingClubs) {
    return (
      <Modal isOpen={true} onClose={onClose} size="sm" showCloseButton={false}>
        <div className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Mark as Read</h2>
          <p className="text-gray-600">Add this book to your past reads</p>
        </div>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Book Display */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-6">
            <div className="flex gap-4">
              {book.cover_url ? (
                <img
                  src={book.cover_url}
                  alt={book.title}
                  className="w-20 h-30 object-cover rounded shadow-sm flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-30 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                <p className="text-sm text-gray-600">by {book.author}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label htmlFor="club" className="block text-sm font-medium text-gray-700 mb-2">
                Where did you read this book? *
              </label>
              <select
                id="club"
                value={selectedClubId || ''}
                onChange={(e) => {
                  const value = e.target.value
                  setSelectedClubId(value === 'personal' ? 'personal' : value === '' ? null : Number(value))
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
              >
                <option value="">Select an option</option>
                <option value="personal">📚 Personal Reading (on my own)</option>
                {clubs.length > 0 && <option disabled>──────────</option>}
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>{club.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startedAt" className="block text-sm font-medium text-gray-700 mb-2">
                  Started Reading (Optional)
                </label>
                <input
                  id="startedAt"
                  type="date"
                  value={startedAt}
                  onChange={(e) => setStartedAt(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                />
              </div>

              <div>
                <label htmlFor="completedAt" className="block text-sm font-medium text-gray-700 mb-2">
                  Completed Reading *
                </label>
                <input
                  id="completedAt"
                  type="date"
                  value={completedAt}
                  onChange={(e) => setCompletedAt(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                />
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading || !selectedClubId || !completedAt}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
            >
              {loading ? 'Marking as Read...' : 'Mark as Read'}
            </button>
          </div>
        </div>
    </Modal>
  )
}
